import React, { useEffect, useRef } from "react";
import PropTypes from "prop-types";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import { Box, Card, CardContent, CircularProgress, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import * as V from "styles/variables";

const useStyles = makeStyles(() => ({
  card: {
    borderRadius: 8,
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.06)",
    border: "1px solid rgba(18, 24, 38, 0.06)",
  },
  chartBox: {
    width: "100%",
    height: 360,
  },
  loadingWrap: {
    minHeight: 360,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
}));

const RegularSeasonDailyTrendChart = ({ trend, loading }) => {
  const classes = useStyles();
  const chartRef = useRef(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      chartRef.current && chartRef.current.getEchartsInstance().resize();
    }, 100);

    return () => clearTimeout(timer);
  }, [trend]);

  echarts.registerTheme("RegularSeasonTheme", {
    color: V.genericColorPallette,
  });

  const [shotColor, reviewOkColor, uniqueShooterColor] = V.genericColorPallette;

  const option = {
    color: [shotColor, reviewOkColor, uniqueShooterColor],
    tooltip: {
      trigger: "axis",
    },
    legend: {
      top: 8,
      right: 8,
    },
    grid: {
      left: 48,
      right: 48, // 24 → 48
      top: 56,
      bottom: 36,
      containLabel: true,
    },
    xAxis: {
      type: "category",
      data: trend.date,
      boundaryGap: true,
      axisTick: {
        alignWithLabel: true,
      },
      axisLabel: {
        color: "#475467",
      },
    },
    yAxis: [
      {
        type: "value",
        axisLabel: {
          color: "#475467",
        },
        splitLine: {
          lineStyle: {
            color: "rgba(148, 163, 184, 0.18)",
          },
        },
      },
      {
        type: "value",
        axisLabel: {
          color: "#475467",
        },
        splitLine: { show: false },
      },
    ],
    series: [
      {
        name: "撮影された数",
        type: "bar",
        data: trend.uniqueShots,
        barWidth: 12,
        barGap: "30%",
        barCategoryGap: "45%",
        yAxisIndex: 0,
        itemStyle: {
          color: shotColor,
        },
      },
      {
        name: "レビューOK数",
        type: "bar",
        data: trend.reviewerOk,
        barWidth: 12,
        barGap: "0%",
        yAxisIndex: 0,
        itemStyle: {
          color: reviewOkColor,
        },
      },
      {
        name: "ユニーク撮影者数",
        type: "line",
        data: trend.uniqueShooters,
        yAxisIndex: 1,
        smooth: false,
        symbolSize: 8,
        lineStyle: {
          width: 3,
          color: uniqueShooterColor,
        },
        itemStyle: {
          color: uniqueShooterColor,
        },
      },
    ],
  };

  return (
    <Card className={classes.card}>
      <CardContent>
        <Typography variant="subtitle1" gutterBottom>
          撮影実績推移
        </Typography>
        {loading ? (
          <Box className={classes.loadingWrap}>
            <CircularProgress size={36} />
          </Box>
        ) : (
          <div className={classes.chartBox}>
            <ReactEcharts
              ref={chartRef}
              option={option}
              theme="RegularSeasonTheme"
              notMerge
              lazyUpdate
              style={{ width: "100%", height: "100%" }}
            />
          </div>
        )}
      </CardContent>
    </Card>
  );
};

RegularSeasonDailyTrendChart.propTypes = {
  trend: PropTypes.shape({
    date: PropTypes.array.isRequired,
    uniqueShots: PropTypes.array.isRequired,
    reviewerOk: PropTypes.array.isRequired,
    uniqueShooters: PropTypes.array.isRequired,
  }).isRequired,
  loading: PropTypes.bool,
};

export default RegularSeasonDailyTrendChart;
